const useApi = () => {
	const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";
	const appId = process.env.REACT_APP_APPID || "default-app-id";

	/**
	 * Makes a generic API call.
	 *
	 * @param {string} endpoint - The API endpoint.
	 * @param {Object} [options={}] - Options for useApi.
	 * @returns {Promise<any>} Parsed JSON response.
	 * @throws {Error} When the API call fails.
	 */
	const baseApiCall = async (endpoint, options = {}) => {
		const url = `${baseUrl}${endpoint}`;
		const headers = {
			"x-app-id": appId,
			...options.headers
		};
		const apiOptions = {
			credentials: "include",
			headers,
			...options
		};

		try {
			const response = await useApiCall(url, apiOptions);
			if (!response.ok) {
				const errorText = await response.json();
				throw new Error(errorText?.error?.message);
			}
			return await response.json();
		} catch (error) {
			console.error("API call failed:", error);
			throw error;
		}
	};

	/**
	 * Wrapper function that replaces the fetch call.
	 * This function should be implemented to call your actual API method.
	 *
	 * @param {string} url - Full API endpoint.
	 * @param {Object} options - Options for the API call.
	 * @returns {Promise<Response>} Response from the API call.
	 */
	const useApiCall = async (url, options) => {
		// Replace this implementation with your actual useApi call.
		return await fetch(url, options);
	};

	/**
	 * Parses query parameters into a query string.
	 *
	 * @param {Object} queryParams - Query parameters as key-value pairs.
	 * @returns {string} The formatted query string.
	 */
	const parseQueryParams = (queryParams) => {
		return queryParams && Object.keys(queryParams).length
			? "?" + new URLSearchParams(queryParams).toString()
			: "";
	};

	return {
		/**
		 * Base API call method.
		 * @type {function}
		 */
		baseApiCall,
		/**
		 * API endpoints for email campaigns.
		 */
		emailCampaigns: {
			/**
			 * Searches for email campaigns.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/email-campaigns${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves an email campaign by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Campaign identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/email-campaigns/${id}`),
			/**
			 * Creates a new email campaign.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new campaign.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/email-campaigns", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing email campaign.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Campaign identifier.
			 * @param {Object} params.data - Updated campaign data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/email-campaigns/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes an email campaign.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Campaign identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/email-campaigns/${id}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for email templates.
		 */
		emailTemplates: {
			/**
			 * Searches for email templates.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/email/template${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves an email template by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Template identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/email/template/${id}`),
			/**
			 * Creates a new email template.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new template.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/email/template", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing email template.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Template identifier.
			 * @param {Object} params.data - Updated template data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/email-templates/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes an email template.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Template identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/email-templates/${id}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for email logs.
		 */
		emailLogs: {
			/**
			 * Searches for email logs.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/email-logs${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves an email log by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Log identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/email-logs/${id}`),
			/**
			 * Deletes an email log.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Log identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/email-logs/${id}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for user management.
		 */
		user: {
			/**
			 * Searches for users.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/user${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves a user by ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - User identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) => baseApiCall(`/user/${id}`),
			/**
			 * Creates a new user.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new user.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/user", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing user.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - User identifier.
			 * @param {Object} params.data - Updated user data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/user/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes a user.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - User identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/user/${id}`, {
					method: "DELETE"
				}),
			/**
			 * Logs out the user.
			 *
			 * @returns {Promise<any>}
			 */
			logout: async () =>
				baseApiCall("/auth/logout", {
					method: "GET"
				}),
			/**
			 * Validates the user.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.verificationToken - Verification token.
			 * @returns {Promise<any>}
			 */
			validate: async ({ verificationToken }) =>
				baseApiCall("/auth/validate", {
                    method: "GET",
                    headers: {
                      Authorization: `Bearer ${verificationToken}`
                    }
				}),
			/**
			 * Sets the default tenant for the user.
			 * Legacy endpoint retained for backward compatibility, prefer tenant.setDefault().
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.tenantId - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			setDefaultTenant: async ({ tenantId }) =>
				baseApiCall(`/user/default/tenant/${tenantId}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({})
				})
		},
		/**
		 * API endpoints for tenant management.
		 */
		tenant: {
			/**
			 * Searches for tenants.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return await baseApiCall(`/tenant${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves a tenant by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) => baseApiCall(`/tenant/${id}`),
			/**
			 * Creates a new tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new tenant.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/tenant", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Tenant identifier.
			 * @param {Object} params.data - Updated tenant data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/tenant/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes a tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/tenant/${id}`, {
					method: "DELETE"
				}),
			/**
			 * Switches the active tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			switch: async ({ id }) =>
				baseApiCall(`/tenant/switch?tenantId=${id}`, {
					method: "GET"
				}),
			/**
			 * Accepts an invite to a tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Invite identifier.
			 * @returns {Promise<any>}
			 */
			acceptInvite: async ({ id }) =>
				baseApiCall(`/tenant/acceptInvite/${id}`, {
					method: "GET"
				}),
			/**
			 * Invites a user to the tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Invitation data.
			 * @returns {Promise<any>}
			 */
			invite: async ({ data }) =>
				baseApiCall("/tenant/invite", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Adds a new organization/tenant.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.orgName - Name of the organization.
			 * @returns {Promise<Object>} Contains success, new organization ID, and tenants list.
			 */
			addOrganization: async ({ orgName }) => {
				const createResult = await baseApiCall("/tenant", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({ name: orgName })
				});
				const tenantResults = await baseApiCall("/tenant?pageOffset=0&pageSize=5");
				const options = tenantResults.rows.map((x) => ({
					label: x.name,
					value: x._id
				}));
				return { success: true, newOrgId: createResult._id, tenants: options };
			},
			/**
			 * Sets the current user's default tenant (new endpoint).
			 * Backend: PUT /tenant/default with body { tenantId }
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.tenantId - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			setDefault: async ({ tenantId }) =>
				baseApiCall("/tenant/default", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({ tenantId })
				})
		},
		/**
		 * API endpoints for organization management.
		 * These endpoints manage organizations which can contain multiple tenants and members.
		 *
		 * Latest controller methods implemented from the backend organization controller:
		 * - GET    /organization
		 * - GET    /organization/:id
		 * - POST   /organization
		 * - PUT    /organization/:id
		 * - PUT    /organization/invite
		 * - DELETE /organization/member/:orgId/:userId
		 * - PUT    /organization/tenant
		 * - PUT    /organization/tenant/invite
		 * - PUT    /organization/tenant/invite-by-email
		 * - POST   /organization/tenant/invite-email
		 * - DELETE /organization/tenant/:orgId/:tenantId
		 * - PUT    /organization/default
		 */
		organization: {
			/**
			 * Searches for organizations that the current user is a member of.
			 * Supports optional fuzzy name filtering and pagination via query parameters.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters (e.g., { organizationNameFilter, pageOffset, pageSize }).
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/organization${parseQueryParams(queryParams)}`);
			},
			/**
			 * Retrieves a single organization by its ID, including member and tenant details.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Organization identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/organization/${id}`),
			/**
			 * Creates a new organization. The creating user is assigned as Owner.
			 * Optional data.tenantIds can pre-attach tenants.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { name, tenantIds?: string[] }).
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/organization", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an organization's name. Requires Owner/Admin in the organization.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Organization identifier.
			 * @param {Object} params.data - Payload (e.g., { name }).
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/organization/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Invites a user to an organization by email. If the user does not exist, a shadow account is created.
			 * Requires Owner/Admin in the target organization.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { firstName, lastName, email, organizationId, role? }).
			 * @returns {Promise<any>}
			 */
			invite: async ({ data }) =>
				baseApiCall("/organization/invite", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Removes a member from an organization. Requires Owner/Admin.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.orgId - Organization identifier.
			 * @param {string} params.userId - User identifier of the member to remove.
			 * @returns {Promise<any>}
			 */
			removeMember: async ({ orgId, userId }) =>
				baseApiCall(`/organization/member/${orgId}/${userId}`, {
					method: "DELETE"
				}),
			/**
			 * Adds a tenant to an organization. Requires Owner/Admin.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { organizationId, tenantId }).
			 * @returns {Promise<any>}
			 */
			addTenant: async ({ data }) =>
				baseApiCall("/organization/tenant", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Adds a tenant to an organization using a tenant invite code.
			 * Note: orgAccessCode is internal and not exposed in API responses or emails.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { orgAccessCode, tenantId? }).
			 * @returns {Promise<any>}
			 */
			addTenantByInviteCode: async ({ data }) =>
				baseApiCall("/organization/tenant/invite", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Links a tenant to an organization by looking up a user's email.
			 * Requires Owner/Admin access to the organization.
			 *
			 * If data.tenantId is omitted, backend will attempt to find a single eligible tenant
			 * for the user by email (Admin/Owner, non-personal). If multiple candidates exist,
			 * the backend will return an error with candidates listed.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { organizationId, email, tenantId? }).
			 * @returns {Promise<any>}
			 */
			addTenantByEmail: async ({ data }) =>
				baseApiCall("/organization/tenant/invite-by-email", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Sends an email invitation to a user with general instructions to link their tenant
			 * to this organization using the email-based flow. Does NOT expose any access code.
			 * Requires Owner/Admin access to the organization.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Payload (e.g., { organizationId, email, firstName?, lastName? }).
			 * @returns {Promise<any>}
			 */
			inviteTenantByEmail: async ({ data }) =>
				baseApiCall("/organization/tenant/invite-email", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Removes a tenant from an organization. Requires Owner/Admin.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.orgId - Organization identifier.
			 * @param {string} params.tenantId - Tenant identifier to remove.
			 * @returns {Promise<any>}
			 */
			removeTenant: async ({ orgId, tenantId }) =>
				baseApiCall(`/organization/tenant/${orgId}/${tenantId}`, {
					method: "DELETE"
				}),
			/**
			 * Sets the current user's default organization (new endpoint).
			 * Backend: PUT /organization/default with body { organizationId }
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.organizationId - Organization identifier.
			 * @returns {Promise<any>}
			 */
			setDefault: async ({ organizationId }) =>
				baseApiCall("/organization/default", {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({ organizationId })
				})
		},
		/**
		 * API endpoints for checking paywall status.
		 */
		paywall: {
			/**
			 * Checks if the paywall is active.
			 *
			 * @returns {Promise<any>}
			 */
			checkPaid: async () =>
				baseApiCall("/auth/paywall", {
					method: "GET"
				})
		},
		/**
		 * API endpoints for payments, payment links, and subscription status.
		 */
		payment: {
			/**
			 * Retrieves available Stripe Payment Links for the current user.
			 * Each link includes a client_reference_id that ties the checkout to the current user.
			 *
			 * Backend: GET /payment/link
			 *
			 * @returns {Promise<{paymentLinks: Array<{_id: string, link: string, signUpType: ('client'|'organization'), features: string[]}>}>}
			 */
			getLinks: async () =>
				baseApiCall("/payment/link", {
					method: "GET"
				}),
			/**
			 * Retrieves the current user's subscription status and feature entitlements.
			 *
			 * Backend: GET /payment/subscription/status
			 *
			 * Expected response (fields may be null/omitted depending on state):
			 * {
			 *   hasActivePaidSubscription: boolean,
			 *   subscription: {
			 *     id: string|null,
			 *     status: string|null,
			 *     customerId: string|null,
			 *     cancelAtPeriodEnd: boolean|null,
			 *     cancelAt: number|null,              // epoch seconds when cancellation takes effect (if scheduled)
			 *     currentPeriodStart: number|null,    // epoch seconds
			 *     currentPeriodEnd: number|null,      // epoch seconds
			 *     daysRemainingUntilCancel: number|null // optional backend-computed remaining days
			 *   },
			 *   plan?: { nickname?: string|null, productName?: string|null } | null,
			 *   price?: { unitAmount?: number|null, currency?: string|null, interval?: string|null } | null,
			 *   features: string[],
			 *   source: {
			 *     paymentLinkId: string|null,
			 *     paymentLinkUrl: string|null,
			 *     paymentLinkDocId: string|null
			 *   }
			 * }
			 *
			 * @returns {Promise<any>}
			 */
			getSubscriptionStatus: async () =>
				baseApiCall("/payment/subscription/status", {
					method: "GET"
				}),
			/**
			 * Creates a Stripe Billing Portal session for the current user and returns the portal URL.
			 *
			 * Backend: POST /payment/subscription/manage
			 * Body: { returnUrl?: string } // optional; where Stripe returns the user upon portal exit
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} [params.returnUrl] - Optional return URL to use when user exits the portal.
			 * @returns {Promise<{ message: string, portalUrl: string|null }>}
			 */
			manageSubscription: async ({ returnUrl } = {}) =>
				baseApiCall("/payment/subscription/manage", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({ returnUrl })
				})
		},
		/**
		 * API endpoints for permission management.
		 */
		permission: {
			/**
			 * Searches for permissions.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/permission${parseQueryParams(queryParams)}`);
			},
			/**
			 * Creates a new permission.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new permission.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/permission", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing permission.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Permission identifier.
			 * @param {Object} params.data - Updated permission data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/permission/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes a permission.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Permission identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/permission/${id}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for role management.
		 */
		role: {
			/**
			 * Searches for roles.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/role${parseQueryParams(queryParams)}`);
			},
			/**
			 * Creates a new role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new role.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/role", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Updates an existing role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Role identifier.
			 * @param {Object} params.data - Updated role data.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/role/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Role identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/role/${id}`, {
					method: "DELETE"
				}),
			/**
			 * Adds a permission to a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param {string} params.permissionId - Permission identifier.
			 * @returns {Promise<any>}
			 */
			addPermission: async ({ roleId, permissionId }) =>
				baseApiCall(`/role/${roleId}/permission/${permissionId}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({})
				}),
			/**
			 * Removes a permission from a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param{string} params.permissionId - Permission identifier.
			 * @returns {Promise<any>}
			 */
			removePermission: async ({ roleId, permissionId }) =>
				baseApiCall(`/role/${roleId}/permission/${permissionId}`, {
					method: "DELETE"
				}),
			/**
			 * Adds a user to a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param {string} params.userId - User identifier.
			 * @returns {Promise<any>}
			 */
			addUser: async ({ roleId, userId }) =>
				baseApiCall(`/role/${roleId}/user/${userId}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({})
				}),
			/**
			 * Removes a user from a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param {string} params.userId - User identifier.
			 * @returns {Promise<any>}
			 */
			removeUser: async ({ roleId, userId }) =>
				baseApiCall(`/role/${roleId}/user/${userId}`, {
					method: "DELETE"
				}),
			/**
			 * Adds a tenant to a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param {string} params.tenantId - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			addTenant: async ({ roleId, tenantId }) =>
				baseApiCall(`/role/${roleId}/tenant/${tenantId}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({})
				}),
			/**
			 * Removes a tenant from a role.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.roleId - Role identifier.
			 * @param {string} params.tenantId - Tenant identifier.
			 * @returns {Promise<any>}
			 */
			removeTenant: async ({ roleId, tenantId }) =>
				baseApiCall(`/role/${roleId}/tenant/${tenantId}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for build management.
		 */
		build: {
			/**
			 * Searches for builds.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/build${parseQueryParams(queryParams)}`, {
					method: "GET"
				});
			},
			/**
			 * Creates a new build.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new build.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/build", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Runs a build.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Build identifier.
			 * @returns {Promise<any>}
			 */
			run: async ({ id }) =>
				baseApiCall(`/build/run/${id}`, {
					method: "GET"
				}),
			/**
			 * Deletes a build.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Build identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/build/${id}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoints for configuration.
		 */
		config: {
			/**
			 * Fetches the configuration data.
			 *
			 * @returns {Promise<any>}
			 */
			fetch: async () =>
				baseApiCall("/config", {
					method: "GET"
				})
		},
		/**
		 * API endpoints for application management.
		 */
		application: {
			/**
			 * Retrieves an application by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.id - Application identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/application/${id}`, {
					method: "GET"
				}),
			/**
			 * Creates a new application.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new application.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/application", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				})
		},
		/**
		 * API endpoints for environment details.
		 */
		environment: {
			/**
			 * Creates a new environment.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.data - Data for the new environment, e.g., { name, applicationId }.
			 * @returns {Promise<any>}
			 */
			create: async ({ data }) =>
				baseApiCall("/environment", {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Retrieves environment details by its ID.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string|number} params.id - Environment identifier.
			 * @returns {Promise<any>}
			 */
			getById: async ({ id }) =>
				baseApiCall(`/environment/${id}`, {
					method: "GET"
				}),
			/**
			 * Updates environment details.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string|number} params.id - Environment identifier.
			 * @param {Object} params.data - Data to update.
			 * @returns {Promise<any>}
			 */
			update: async ({ id, data }) =>
				baseApiCall(`/environment/${id}`, {
					method: "PUT",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				}),
			/**
			 * Deletes an environment.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string|number} params.id - Environment identifier.
			 * @returns {Promise<any>}
			 */
			delete: async ({ id }) =>
				baseApiCall(`/environment/${id}`, {
					method: "DELETE",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({})
				}),
			/**
			 * Searches for environments.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.queryParams - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			search: async ({ queryParams }) => {
				return baseApiCall(`/environment${parseQueryParams(queryParams)}`, {
					method: "GET"
				});
			}
		},
		/**
		 * API endpoints for environment URI management.
		 */
		environmentURI: {
			/**
			 * Adds a new URI to an environment.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {(string|number)} params.environmentId - Environment identifier.
			 * @param {Object} params.data - Data for the new URI, e.g., { uriKey, uriValue }.
			 * @returns {Promise<any>} Parsed JSON response.
			 */
			add: async ({ environmentId, data }) =>
				baseApiCall(`/environment/${environmentId}/uri`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify(data)
				})
		},
		/**
		 * API endpoints for accepted domain management.
		 */
		acceptedDomain: {
			/**
			 * Adds a new accepted domain for a given environment.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {(string|number)} params.environmentId - Environment identifier.
			 * @param {string} params.acceptedDomain - The accepted domain to add.
			 * @returns {Promise<any>} Parsed JSON response.
			 */
			add: async ({ environmentId, acceptedDomain }) =>
				baseApiCall(`/environment/${environmentId}/domain`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json"
					},
					body: JSON.stringify({ acceptedDomain: acceptedDomain })
				}),
			/**
			 * Deletes an accepted domain for a given application and environment.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {string} params.applicationId - Application identifier.
			 * @param {string} params.environmentName - Environment name.
			 * @param {string} params.acceptedDomain - The accepted domain to be deleted.
			 * @returns {Promise<any>} Response after the deletion is complete.
			 */
			delete: async ({ applicationId, environmentName, acceptedDomain }) =>
				baseApiCall(`/application/${applicationId}/environment/${environmentName}/domain/${acceptedDomain}`, {
					method: "DELETE"
				})
		},
		/**
		 * API endpoint for authentication initialization.
		 */
		auth: {
			/**
			 * Initializes authentication by calling /auth/init.
			 *
			 * @param {Object} params - Parameters object.
			 * @param {Object} params.params - Query parameters as key-value pairs.
			 * @returns {Promise<any>}
			 */
			init: async ({ params }) =>
				baseApiCall(`/auth/init${parseQueryParams(params)}`, {
					method: "GET"
				}),
            profile: async () =>
                baseApiCall(`/auth/profile`, {
                    method: "GET"
                }),
		}
	};
};

export default useApi;